import {
    ActionCreatorWithPayload,
    createAction,
    Dispatch,
    ListenerEffect,
    ThunkDispatch,
    UnknownAction,
} from '@reduxjs/toolkit';

type DebouncedValuePayload<P> = { value: P; immediate?: boolean };

export type ActionCreatorForDebouncedValue<P> = ActionCreatorWithPayload<DebouncedValuePayload<P>>;

type DebouncedValueSetup<P, State, DispatchType extends Dispatch> = {
    setValueAction: ActionCreatorForDebouncedValue<P>;
    setDebouncedValueAction: ActionCreatorWithPayload<P>;
    listener: {
        actionCreator: ActionCreatorForDebouncedValue<P>;
        effect: ListenerEffect<ReturnType<ActionCreatorWithPayload<DebouncedValuePayload<P>>>, State, DispatchType>;
    };
};

export const createDebouncedValue = <
    P,
    State = unknown,
    DispatchType extends Dispatch = ThunkDispatch<State, unknown, UnknownAction>,
>({
    typePrefix,
    debounceTime,
}: {
    typePrefix: string;
    debounceTime: number;
}): DebouncedValueSetup<P, State, DispatchType> => {
    const setValueAction = createAction<DebouncedValuePayload<P>>(`${typePrefix}/setValue`);
    const setDebouncedValueAction = createAction<P>(`${typePrefix}/setDebouncedValue`) as ActionCreatorWithPayload<P>;

    return {
        setValueAction,
        setDebouncedValueAction,
        listener: {
            actionCreator: setValueAction,
            effect: async ({ payload: { value, immediate = false } }, listenerApi) => {
                listenerApi.cancelActiveListeners();

                if (!immediate) {
                    await listenerApi.delay(debounceTime);
                }

                listenerApi.dispatch(setDebouncedValueAction(value));
            },
        },
    };
};
